import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, TrendingDown } from "lucide-react";

export default function Finance() {
  const [price, setPrice] = useState(85000);
  const [down, setDown] = useState(10000);
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(60);

  const monthly = useMemo(() => {
    const P = Math.max(0, price - down);
    const r = rate / 100 / 12;
    const n = term;
    if (r === 0) return P / n;
    return (P * r) / (1 - Math.pow(1 + r, -n));
  }, [price, down, rate, term]);

  const totalInterest = useMemo(() => monthly * term - (price - down), [monthly, term, price, down]);

  const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

  return (
    <div data-testid="page-finance" className="pt-32 pb-24 max-w-[1500px] mx-auto px-6 md:px-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="overline flex items-center gap-3">
          <Calculator size={14} /> Finance
        </div>
        <h1 className="font-display text-6xl md:text-8xl mt-3 tracking-tighter">
          Design your
          <br />
          <span className="text-[var(--primary)]">payment plan.</span>
        </h1>
        <p className="text-white/60 mt-6 max-w-xl">
          Move the sliders. See real numbers. This calculator is for estimation — actual APR determined by lender at approval.
        </p>
      </motion.div>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10 items-start">
        <div className="space-y-8 bento-cell md:p-10">
          <Slider label="Vehicle Price" value={price} setValue={setPrice} min={5000} max={500000} step={1000} format={fmt} testid="fin-price" />
          <Slider label="Down Payment" value={down} setValue={setDown} min={0} max={Math.max(1000, price - 1000)} step={500} format={fmt} testid="fin-down" />
          <Slider label="Interest Rate (APR)" value={rate} setValue={setRate} min={0.5} max={20} step={0.1} format={(v) => `${v.toFixed(2)}%`} testid="fin-rate" />
          <Slider label="Term (months)" value={term} setValue={setTerm} min={12} max={84} step={12} format={(v) => `${v} mo`} testid="fin-term" />
        </div>

        <div className="glass rounded-2xl p-8 sticky top-28" data-testid="fin-summary">
          <div className="overline flex items-center gap-2"><TrendingDown size={12} /> Estimated Payment</div>
          <div className="font-display text-6xl mt-3 tracking-tighter text-[var(--primary)]">
            {fmt(monthly)}
          </div>
          <div className="text-white/50 text-sm mt-1">per month</div>

          <div className="mt-8 space-y-3 pt-6 border-t border-white/10 text-sm">
            <Row k="Vehicle price" v={fmt(price)} />
            <Row k="Down payment" v={fmt(down)} />
            <Row k="Financed amount" v={fmt(price - down)} />
            <Row k="Total interest" v={fmt(totalInterest)} />
            <Row k="Total paid" v={fmt(monthly * term + down)} highlight />
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, highlight }) => (
  <div className={`flex items-center justify-between ${highlight ? "text-white font-semibold pt-3 border-t border-white/10" : "text-white/60"}`}>
    <span>{k}</span>
    <span>{v}</span>
  </div>
);

const Slider = ({ label, value, setValue, min, max, step, format, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <label className="!mb-0">{label}</label>
      <div className="font-display text-2xl tracking-tighter">{format(value)}</div>
    </div>
    <input
      data-testid={testid}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => setValue(Number(e.target.value))}
      className="w-full h-1 accent-[var(--primary)]"
      style={{ padding: 0, background: "linear-gradient(to right, var(--primary) 0%, var(--primary) " + (((value - min) / (max - min)) * 100) + "%, rgba(255,255,255,0.1) " + (((value - min) / (max - min)) * 100) + "%, rgba(255,255,255,0.1) 100%)", borderRadius: 999, border: "none" }}
    />
    <div className="flex justify-between text-[10px] text-white/30 mt-2">
      <span>{format(min)}</span>
      <span>{format(max)}</span>
    </div>
  </div>
);
